import { isCancel } from 'axios'
import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type AxiosOptions } from 'shared/api'
import { TableStore, type IRowDefault } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import type { IAnalyticsParams } from 'entities/Analytics/api/types'
import { MessagesAnalyticsApi } from 'entities/Analytics'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IUser } from 'entities/Users'
import type { Tag } from 'entities/Tags/model/Tag'
import type { IInboxCombine } from 'entities/Inbox'
import type { Phone } from 'entities/Phone/model/Phone'
import { OutboundSource } from 'entities/OutboundSource/model/OutboundSource'
import { SearchTypesEnum } from 'widgets/SearchDropdown'

export type AnalyticsComparisonEntity = IUser | Tag | IInboxCombine | Phone | OutboundSource

export interface IComparisonData<TMetricsKeys extends string> {
  id: string | number
  name: string
  metrics: Record<TMetricsKeys, number>
  breakdownEntity?: AnalyticsComparisonEntity
}

export interface IAnalyticsComparisonTableConfig<
  TMetricsKeys extends string,
  TRow extends IRowDefault
> {
  tableKey: string
  getAnalytics: (
    params: IAnalyticsParams,
    options?: AxiosOptions
  ) => Promise<{ data?: { statistic?: Array<{ key: string; value: number }> } }>
  createRow: (data: IComparisonData<TMetricsKeys>) => TRow
  createMetricsRecord: () => Record<TMetricsKeys, number>
}

export class AnalyticsComparisonTableStore<TMetricsKeys extends string, TRow extends IRowDefault> {
  private readonly _tableStore: TableStore<TRow>
  private _disposeReaction: IReactionDisposer | null = null

  private _error: string | null = null
  private _comparisonData: IComparisonData<TMetricsKeys>[] = []

  constructor(
    private _config: IAnalyticsComparisonTableConfig<TMetricsKeys, TRow>,
    private _analyticsFiltersParams: () => IAnalyticsParams,
    private _breakdownFilterType: () => SearchTypesEnum | null,
    private _entities: IEntitiesStore
  ) {
    this._tableStore = new TableStore<TRow>({
      key: _config.tableKey,
      useColumnsCache: true,
    })
    makeAutoObservable(this)
  }

  private _isValidMetricKey(
    key: string,
    metrics: Record<TMetricsKeys, number>
  ): key is TMetricsKeys {
    return key in metrics
  }

  private _normalizeFilterValue(value: string | string[] | undefined): string[] {
    if (value === undefined) {
      return []
    }
    if (typeof value === 'string') {
      return [value]
    }
    return value
  }

  private _getFilterKey(type: SearchTypesEnum): string {
    switch (type) {
      case SearchTypesEnum.members:
        return 'members'
      case SearchTypesEnum.tags:
        return 'tags'
      case SearchTypesEnum.teams:
        return 'inboxes'
      case SearchTypesEnum.numbers:
        return 'numbers'
      case SearchTypesEnum.outbound_sources:
        return 'sources'
      default:
        return ''
    }
  }

  private _getBreakdownItems(
    breakdownType: SearchTypesEnum | null,
    params: IAnalyticsParams
  ): {
    ids: Array<string>
    type: SearchTypesEnum
  } | null {
    if (!breakdownType) return null

    switch (breakdownType) {
      case SearchTypesEnum.members:
        return { ids: this._normalizeFilterValue(params.filter?.members), type: breakdownType }
      case SearchTypesEnum.tags:
        return { ids: this._normalizeFilterValue(params.filter?.tags), type: breakdownType }
      case SearchTypesEnum.teams:
        return { ids: this._normalizeFilterValue(params.filter?.inboxes), type: breakdownType }
      case SearchTypesEnum.numbers:
        return { ids: this._normalizeFilterValue(params.filter?.numbers), type: breakdownType }
      case SearchTypesEnum.outbound_sources:
        return {
          ids: this._normalizeFilterValue(params.filter?.sources),
          type: breakdownType,
        }
      default:
        return null
    }
  }

  private async _fetchBreakdownEntities(
    ids: Array<string>,
    type: SearchTypesEnum
  ): Promise<Array<AnalyticsComparisonEntity>> {
    const numericIds = ids.map((id) => Number(id))

    switch (type) {
      case SearchTypesEnum.members: {
        try {
          return this._entities.usersStore.getItemsByIds(numericIds)
        } catch (error) {
          console.error('Failed to fetch members:', error)
          return []
        }
      }
      case SearchTypesEnum.tags: {
        try {
          return this._entities.tagsStore.fetchTags(numericIds)
        } catch (error) {
          console.error('Failed to fetch tags:', error)
          return []
        }
      }
      case SearchTypesEnum.numbers: {
        try {
          return await this._entities.numbersStore.fetchNumbersByIds(numericIds)
        } catch (error) {
          console.error('Failed to fetch numbers:', error)
          return []
        }
      }
      case SearchTypesEnum.teams: {
        try {
          return await this._entities.inboxesStore.fetchInboxesByIds(numericIds)
        } catch (error) {
          console.error('Failed to fetch teams:', error)
          return []
        }
      }
      case SearchTypesEnum.outbound_sources: {
        try {
          const { data } = await MessagesAnalyticsApi.getOutboundSources()
          const filteredData = data.filter((item) => ids.includes(item.key))
          return filteredData.map((item) => new OutboundSource(item))
        } catch (error) {
          console.error('Failed to fetch outbound sources:', error)
          return []
        }
      }
      default:
        return []
    }
  }

  private _getFallbackName(id: string, type: SearchTypesEnum): string {
    switch (type) {
      case SearchTypesEnum.members:
        return `Member ${id}`
      case SearchTypesEnum.tags:
        return `Tag ${id}`
      case SearchTypesEnum.teams:
        return `Team ${id}`
      case SearchTypesEnum.numbers:
        return `Number ${id}`
      case SearchTypesEnum.outbound_sources:
        return `Source ${id}`
      default:
        return `Item ${id}`
    }
  }

  init = () => {
    this._initReaction()
  }

  private _initReaction = () => {
    this._disposeReaction?.()
    this._disposeReaction = reaction(
      () => ({
        filters: this._analyticsFiltersParams(),
      }),
      () => {
        this._requestLoadComparisonData.debounceLoadData()
      }
    )
  }

  private _requestLoadComparisonData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      const breakdownItems = this._getBreakdownItems(
        this._breakdownFilterType(),
        this._analyticsFiltersParams()
      )
      if (!breakdownItems || breakdownItems.ids.length === 0) {
        runInAction(() => {
          this._comparisonData = []
        })
        return
      }

      this._tableStore.setLoading(true)

      runInAction(() => {
        this._error = null
      })

      const baseParams = this._analyticsFiltersParams()
      const filterKey = this._getFilterKey(breakdownItems.type)

      const requests = breakdownItems.ids.map((id) => {
        const itemParams: IAnalyticsParams = {
          ...baseParams,
          filter: {
            ...baseParams.filter,
            [filterKey]: [String(id)],
          },
        }

        return this._config.getAnalytics(itemParams, {
          cancelToken: cancelRequest.token,
        })
      })

      const [responses, entities] = await Promise.all([
        Promise.all(requests),
        this._fetchBreakdownEntities(breakdownItems.ids, breakdownItems.type),
      ])

      const comparisonData: IComparisonData<TMetricsKeys>[] = breakdownItems.ids.map(
        (id, index) => {
          const response = responses[index]
          const metrics = this._config.createMetricsRecord()

          if (response?.data?.statistic) {
            response.data.statistic.forEach((stat) => {
              if (this._isValidMetricKey(stat.key, metrics)) {
                metrics[stat.key] = stat.value
              }
            })
          }

          const breakdownEntity = entities.find((entity) => String(entity.id) === String(id))

          return {
            id,
            name: this._getFallbackName(id, breakdownItems.type),
            metrics,
            breakdownEntity,
          }
        }
      )

      runInAction(() => {
        this._comparisonData = comparisonData
      })
    },
    errorCallback: (error) => {
      if (!isCancel(error)) {
        this._error = 'Failed to load comparison data'
        console.error('Failed to load comparison data:', error)
      }
    },
    finallyCallback: () => {
      this._tableStore.setLoading(false)
    },
  })

  loadComparisonData = async () => {
    return this._requestLoadComparisonData.loadData()
  }

  get tableStore() {
    return this._tableStore
  }

  get error() {
    return this._error
  }

  get comparisonRows() {
    return this._comparisonData.map((data) => this._config.createRow(data))
  }

  get breakdownFilterType() {
    return this._breakdownFilterType()
  }

  dispose = () => {
    this._disposeReaction?.()
    this._requestLoadComparisonData.dispose()
    this._tableStore.dispose()
  }
}
